import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useAuthentication } from '@connect-x-app/hybrid-state';
import { getAccessToken } from '@connect-x-app/hybrid-utils';
import { jwtDecode } from 'jwt-decode';
import { LDMultiKindContext, LDProvider } from 'launchdarkly-react-client-sdk';

// The claims of the Connect IdP access token that LaunchDarkly targets on (AVIO-51744).
type Claims = { customer?: string; environment?: string };

export const LaunchDarklyWrapper = ({ children }: PropsWithChildren) => {
  const { user } = useAuthentication();
  const [customer, setCustomer] = useState<string>();
  const [environment, setEnvironment] = useState<string>();

  // Read whenever the user is (re)loaded: an expired token is renewed in place by the first request, so on such a load
  // the token is there only once auth/me has answered. Claims, once known, are kept: clearing them would make the
  // provider start a second client when they came back.
  useEffect(() => {
    const readClaims = async () => {
      const token = await getAccessToken();
      const claims: Claims = token ? jwtDecode<Claims>(token) : {};
      if (claims.customer && claims.environment) {
        setCustomer(claims.customer);
        setEnvironment(claims.environment);
      }
    };

    readClaims();
  }, [user]);

  // Only a user with a web key, and both claims, give LaunchDarkly a context to start with.
  const context: LDMultiKindContext | undefined = useMemo(() => {
    if (!user?.externalId || !user.featureFlagWebKey || !customer || !environment) {
      return undefined;
    }
    return {
      customer: { key: customer },
      environment: { key: environment },
      kind: 'multi',
      user: { key: user.externalId },
    };
  }, [customer, environment, user?.externalId, user?.featureFlagWebKey]);

  // The provider is there from the first render, so the app below never remounts when the user or the claims arrive.
  // With deferInitialization it starts LaunchDarkly once it gets a context; until then every flag is off.
  return (
    <LDProvider clientSideID={user?.featureFlagWebKey ?? ''} context={context} deferInitialization>
      {children}
    </LDProvider>
  );
};
